import React, {useCallback, useEffect, useState} from 'react';
import useTextAreaValidation from '@/components/atoms/TextArea/useTextAreaValidation-hook';
import {TextArea as PepsicoDsTextArea} from '@pepsico-ds/ui';
import PropTypes from 'prop-types';

/**
 * TextArea component renders a text area input field with various properties.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.value - The current value of the text area.
 * @param {Function} props.updateValue - Function to update the value of the text area.
 * @param {string} props.label - The label for the text area.
 * @param {Object} props.uiSchema - UI schema object containing additional configurations.
 * @param {string} [props.uiSchema.helperText=''] - Helper text to display below the text area.
 * @param {string} [props.uiSchema.className=''] - Additional class names for styling.
 * @param {string} [props.uiSchema.placeholder=''] - Placeholder text for the text area.
 * @param {boolean} [props.uiSchema.showToolbar=false] - Whether to show the toolbar.
 * @param {boolean} [props.disabled=false] - Indicates if the text area is disabled.
 * @param {string} [props.id=''] - Optional additional id to apply to the text area.
 * @param {boolean} [props.trim=true] - Whether to automatically trim whitespace from the value.
 * @param {boolean} [props.uiSchema.showCharacterCount=false] - Whether to show character count.
 * @returns {JSX.Element} The rendered text area component.
 */
const TextArea = ({
  value,
  updateValue = () => {},
  label,
  uiSchema,
  disabled = false,
  id,
  trim = true
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [helperText, setHelperText] = useState(
    () => uiSchema?.helperText || ''
  );
  const [isFocused, setIsFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const {onChange} = useTextAreaValidation(label, uiSchema?.validationRules);

  // Clear validation error
  const clearValidationError = useCallback(
    (val) => {
      setErrorMessage('');
      setHelperText(uiSchema?.helperText || '');
      updateValue(val, false);
    },
    [uiSchema?.helperText, updateValue]
  );

  // Handle validation error
  const handleValidationError = useCallback(
    (error, val) => {
      setErrorMessage(error);
      setHelperText('');
      updateValue(val, true);
    },
    [updateValue]
  );
  const stripHtml = useCallback((html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }, []);

  // Handle onChange
  const handleChange = useCallback(
    (e) => {
      let newValue = e?.target?.value ?? e;
      // Only strip HTML if NOT in rich text mode (showToolbar false)
      if (
        !uiSchema?.showToolbar &&
        typeof newValue === 'string' &&
        newValue.includes('<')
      ) {
        newValue = stripHtml(newValue);
      }
      // In rich text mode, treat visually empty content (e.g. "<p><br></p>")
      // as empty string so the placeholder reappears
      if (
        uiSchema?.showToolbar &&
        typeof newValue === 'string' &&
        stripHtml(newValue).trim() === ''
      ) {
        newValue = '';
      }
      newValue =
        typeof newValue === 'string' && trim ? newValue.trim() : newValue;
      const validationError = onChange(newValue);
      if (validationError) {
        handleValidationError(validationError, newValue);
      } else {
        clearValidationError(newValue);
      }
    },
    [
      onChange,
      trim,
      handleValidationError,
      clearValidationError,
      stripHtml,
      uiSchema?.showToolbar
    ]
  );

  // Handle onBlur
  const handleBlur = useCallback(() => {
    setTouched(true);

    // Validate on every blur, including empty values, so a "required" rule
    // stays visible instead of being wiped by this early-exit.
    const trimmedValue =
      typeof value === 'string' && trim ? value.trim() : (value ?? '');
    const validationError = onChange(trimmedValue);

    if (validationError) {
      handleValidationError(validationError, trimmedValue);
    } else {
      clearValidationError(trimmedValue);
    }

    if (!isFocused) {
      setIsFocused(true);
    }
  }, [
    onChange,
    trim,
    handleValidationError,
    clearValidationError,
    isFocused,
    value
  ]);

  // Effect to validate value locally (without calling updateValue to avoid infinite loops)
  useEffect(() => {
    if (touched || (value && value !== '')) {
      let newValue = value;
      // Only strip HTML if NOT in rich text mode (showToolbar false)
      if (
        !uiSchema?.showToolbar &&
        typeof newValue === 'string' &&
        newValue.includes('<')
      ) {
        newValue = stripHtml(newValue);
      }
      if (typeof newValue === 'string' && trim) {
        newValue = newValue.trim();
      }
      const validationError = onChange(newValue);
      if (validationError) {
        setErrorMessage(validationError);
        setHelperText('');
      } else {
        setErrorMessage('');
        setHelperText(uiSchema?.helperText || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, touched, uiSchema?.showToolbar]);

  let pepsicoDsId = 'text-area';
  let dataTestId = 'text-area';
  if (id) {
    pepsicoDsId = `text-area_${id}`;
    dataTestId = `text-area_${id}`;
  }

  const maxLength = uiSchema?.maxLength || 500;

  const labelProp = uiSchema?.tooltipText
    ? {label, infoIconTooltip: {text: uiSchema.tooltipText}}
    : label;

  return (
    <div style={{position: 'relative', width: '100%'}}>
      <PepsicoDsTextArea
        id={pepsicoDsId}
        data-testid={dataTestId}
        helperText={helperText}
        error={touched ? errorMessage : undefined}
        feedbackType={touched && errorMessage ? 'error' : undefined}
        isDark
        label={labelProp}
        className={`${uiSchema?.className || ''}`}
        value={value || ''}
        disabled={disabled || uiSchema?.readOnly || uiSchema?.disabled}
        placeholder={uiSchema?.placeholder}
        showCharacterCount={uiSchema?.showCharacterCount ?? false}
        showLabelIcon={!!uiSchema?.tooltipText}
        onChange={handleChange}
        onBlur={handleBlur}
        maxLength={maxLength}
        rows={uiSchema?.rows}
        showToolbar={uiSchema?.showToolbar}
        style={{...(uiSchema?.style || {}), width: '100%'}}
      />
    </div>
  );
};

TextArea.propTypes = {
  value: PropTypes.string.isRequired,
  updateValue: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  uiSchema: PropTypes.shape({
    helperText: PropTypes.string,
    tooltipText: PropTypes.string,
    className: PropTypes.string,
    placeholder: PropTypes.string,
    showToolbar: PropTypes.bool,
    showCharacterCount: PropTypes.bool,
    readOnly: PropTypes.bool,
    disabled: PropTypes.bool,
    maxLength: PropTypes.number,
    rows: PropTypes.number,
    validationRules: PropTypes.array
  }),
  disabled: PropTypes.bool,
  id: PropTypes.string,
  trim: PropTypes.bool
};

export default React.memo(TextArea);
